// Licenses — a real table under Operations → Licenses (N-095), holding rows
// filed automatically from forwarded email (N-094, "forward and file") AND
// rows entered by hand. Owner, 2026-09-10: "Licenses needs to be a table,
// just like other things" / "i need these put in a table that lays out what
// it is, year and expiration date and link to its location in drive not
// just a drive folder link."
//
// Mirrors lib/serviceCompliance.ts's own shape deliberately — same
// `fetch-once, filter-by-clinic-in-the-caller` convention as SPC (see
// ServiceCompliance.tsx, which fetches ALL rows under one key and filters by
// `locationCode` per clinic rather than keying the cache per clinic) — so
// ComplianceAlerts.tsx can merge SPC and license alerts off ONE fetch each,
// and CompanyLicenses.tsx (Operations → Licenses) filters the same cached
// rows instead of paying a second key per clinic.
import { authedGet, authedWrite } from './apiClient';
import { persistResource } from './store';
import { daysBetweenIso } from './civilDate';

export const LICENSES_KEY = 'company-licenses';

export interface LicenseRow {
  id: number;
  locationCode: string;
  kind: string;
  issuer: string | null;
  number: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  holder: string | null;
  driveFileId: string | null;
  driveUrl: string | null;
  status: 'FILED' | 'NEEDS_REVIEW';
  createdAt: string;
  // N-100 (owner, 2026-09-10): "i am not sure i want things to be deleted
  // rather removed from a table" — a row moved to Old Licenses stays in this
  // same list (the server query still returns it), just carrying who moved
  // it and when instead of leaving the table.
  movedToOldAt: string | null;
  movedToOldBy: string | null;
}

/** N-100: a row that has been moved to Old Licenses — the narrowed type lets
 * callers read `movedToOldAt` without a null check once this has said yes. */
export function isOldLicense(row: LicenseRow): row is LicenseRow & { movedToOldAt: string } {
  return Boolean(row.movedToOldAt);
}

export interface LicensesData {
  rows: LicenseRow[];
  alertDays: number;
}

export function fetchLicenses(): Promise<LicensesData> {
  return authedGet('/api/v1/company/licenses');
}

/**
 * Days until `expiresOn`, or null when there is none. A license only ever
 * carries `expiresOn` — no due/renewal fallback chain the way SPC's
 * `daysUntilAlert` has (serviceCompliance.ts), because licenses have no
 * equivalent of a due date or a renewal-ordered date.
 */
export function daysUntilExpiry(
  row: Pick<LicenseRow, 'expiresOn'>,
  today: string,
): number | null {
  if (!row.expiresOn) return null;
  return daysBetweenIso(today, row.expiresOn);
}

export function isExpiringSoon(
  row: Pick<LicenseRow, 'expiresOn'>,
  today: string,
  windowDays = 45,
): boolean {
  const days = daysUntilExpiry(row, today);
  return days != null && days <= windowDays;
}

/** Owner, 2026-09-10: licenses expired or expiring within this many days go
 * on the "Needs attention" desk panel (a manager for their clinic, owner and
 * director for every clinic) and colour orange in the Licenses table. */
export const LICENSE_ATTENTION_DAYS = 90;

/** Active (not Old Licenses) rows in `clinics` that are expired or expire
 * within LICENSE_ATTENTION_DAYS, soonest first. */
export function licensesNeedingAttention(
  rows: LicenseRow[],
  clinics: ReadonlySet<string>,
  today: string,
): LicenseRow[] {
  return rows
    .filter((r) => clinics.has(r.locationCode) && !isOldLicense(r) && isExpiringSoon(r, today, LICENSE_ATTENTION_DAYS))
    .sort((a, b) => (daysUntilExpiry(a, today) ?? 99) - (daysUntilExpiry(b, today) ?? 99) || a.kind.localeCompare(b.kind));
}

/** Year column: the year of `issuedOn`, else the year of `expiresOn`, else
 * "—" — a license always has at least one of the two once it is filed, but
 * neither is guaranteed on a hand-entered row still missing detail. */
export function licenseYear(row: Pick<LicenseRow, 'issuedOn' | 'expiresOn'>): string {
  const source = row.issuedOn || row.expiresOn;
  return source ? source.slice(0, 4) : '—';
}

/** Pull a Drive file id out of `/d/<id>/` or `?id=<id>` (or `&id=<id>`) —
 * the two shapes a "Copy link" on a Drive file actually produces. Anything
 * else is refused rather than guessed at (standing rule 8: no fuzzy
 * matching) — the manual-add form shows the caller the refusal directly. */
export function extractDriveFileId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const byPath = /\/d\/([a-zA-Z0-9_-]+)/.exec(trimmed);
  if (byPath) return byPath[1];
  const byQuery = /[?&]id=([a-zA-Z0-9_-]+)/.exec(trimmed);
  if (byQuery) return byQuery[1];
  return null;
}

export type LicenseWrite = {
  locationCode: string;
  kind: string;
  issuer: string;
  number: string;
  issuedOn: string;
  expiresOn: string;
  holder: string;
  driveFileId: string;
};

export async function addLicense(body: LicenseWrite): Promise<{ license: LicenseRow }> {
  return authedWrite('POST', '/api/v1/company/licenses', body);
}

export async function updateLicense(id: number, body: LicenseWrite): Promise<{ license: LicenseRow }> {
  return authedWrite('PATCH', `/api/v1/company/licenses/${id}`, body);
}

export async function deleteLicense(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/licenses/${id}`, {});
}

// N-100 (owner, 2026-09-10, verbatim): "creat on the drive in each licenses
// folder another folder called 'Old Licenses'. then i need a button to
// 'move to old licenses'. only for myself and Candace" — OWNER/DIRECTOR
// gate lives client-side in CompanyLicenses.tsx (canMoveToOld) and
// server-side in src/intake_routes.js (isOwnerOrDirector); this call is
// idempotent — moving an already-moved row is a 200 no-op.
export async function moveLicenseToOld(
  id: number,
): Promise<{ license: LicenseRow; driveMoved: boolean; oldFolderUrl: string | null }> {
  return authedWrite('POST', `/api/v1/company/licenses/${id}/move-to-old`, {});
}

/** "Create the folder now" — used by the Old Licenses panel when the clinic's
 * Licenses folder has no "Old Licenses" child yet. */
export async function ensureOldLicensesFolder(
  locationCode: string,
): Promise<{ locationCode: string; folderId: string; folderUrl: string; created: boolean }> {
  return authedWrite('POST', '/api/v1/company/licenses/old-folder', { locationCode });
}

export async function reloadLicenses(): Promise<void> {
  const data = await fetchLicenses();
  await persistResource(LICENSES_KEY, data);
}

// N-099 (owner, 2026-09-10, verbatim): "Can you go through all the docs in
// licenses folders and log them into the Licenses table?" — reads every
// file already sitting in the clinic's Licenses Drive folder and files one
// through the same license_document.add write the manual form above uses.
// OWNER/DIRECTOR only (src/intake_routes.js's scanCompanyLicensesFolder).
export interface LicenseScanRow {
  id: number;
  fileName: string;
  kind: string;
  expiresOn: string | null;
  confidence: number | null;
}

export interface LicenseScanResult {
  locationCode: string;
  scanned: number;
  added: number;
  alreadyListed: number;
  skippedUnreadable: number;
  rows: LicenseScanRow[];
}

export async function scanLicenses(locationCode: string): Promise<LicenseScanResult> {
  return authedWrite('POST', '/api/v1/company/licenses/scan', { locationCode });
}
